import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DiscountType } from '../../../../../shared/enums/discount-type.enum';
import { InputmaskOptions, InputMaskModule } from '@ngneat/input-mask';
import { AccessGroup, CreateAccessGroup } from '../../../../access-groups/access-group.model';
import { MaskService } from '../../../../../shared/services/mask.service';
import { KeyValuePipe } from '@angular/common';
import { TranslateOrganizationDiscountTypeEnum } from '../../../../../shared/pipes/translate-organization-discount-type-enum.pipe';
import { AccessGroupCarType } from 'src/app/shared/enums/access-group-car-type';
import { EnumToArrayPipe } from '../../../../../shared/pipes/enum-to-array.pipe';
import { TranslateAccessGroupCarTypeEnum } from '../../../../../shared/pipes/translate-access-group-car-type-enum.pipe';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import {
  AccessGroupSharedDiscountService,
  IAccessGroupSharedDiscountSearchResultItem,
} from 'src/app/shared/services/access-group-shared-discount.service';

interface AccessGroupForm {
  name: FormControl<string>;
  amount: FormControl<number | null>;
  carType: FormControl<AccessGroupCarType>;
  discountType: FormControl<DiscountType>;
  sharedDiscountIdentifier: FormControl<string | null>;
}
@Component({
  selector: 'adm-step3-access-groups-form',
  templateUrl: './step3-access-groups-form.component.html',
  styleUrl: './step3-access-groups-form.component.scss',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    InputMaskModule,
    KeyValuePipe,
    TranslateOrganizationDiscountTypeEnum,
    EnumToArrayPipe,
    TranslateAccessGroupCarTypeEnum,
    CustomDropDownArrowDirective,
  ],
})
export class Step3AccessGroupsFormComponent implements OnInit {
  private maskService = inject(MaskService);
  private sharedDiscountService = inject(AccessGroupSharedDiscountService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  @Output() emitCancel = new EventEmitter<void>();
  @Output() save = new EventEmitter<Partial<CreateAccessGroup>>();

  @Input() accessGroup?: AccessGroup;
  @Input() discountType?: DiscountType;
  @Input() carType?: AccessGroupCarType;

  public form: FormGroup<AccessGroupForm>;
  public carTypes: typeof AccessGroupCarType = AccessGroupCarType;

  public priceInputMask: InputmaskOptions<unknown>;

  public discountTypes: typeof DiscountType = DiscountType;

  public sharedDiscounts: IAccessGroupSharedDiscountSearchResultItem[] = [];

  get nameFormControl() {
    return this.form.controls.name;
  }

  get amountFormControl() {
    return this.form.controls.amount;
  }

  get sharedDiscountIdentifierFormControl() {
    return this.form.controls.sharedDiscountIdentifier;
  }

  ngOnInit() {
    const carType = this.accessGroup?.carType || AccessGroupCarType.CompanyCar;
    this.form = new FormGroup<AccessGroupForm>({
      name: new FormControl<string>(this.accessGroup?.name || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(1), Validators.maxLength(200)],
      }),
      carType: new FormControl<AccessGroupCarType>(
        { value: carType, disabled: this.accessGroup != undefined /* Check for both undefined and null */ },
        { nonNullable: true, validators: [Validators.required] }
      ),
      amount: new FormControl<number | null>(this.accessGroup?.amount || null, { validators: [Validators.min(0), Validators.max(100)] }),
      discountType: new FormControl<DiscountType>(
        { value: this.accessGroup?.discountType || this.discountType || DiscountType.FixedAmount, disabled: true },
        { nonNullable: true }
      ),
      sharedDiscountIdentifier: new FormControl<string | null>(this.accessGroup?.sharedDiscountIdentifier ?? null),
    });

    this.priceInputMask = this.maskService.priceInputMask;

    // Fetch shared discounts based on discountType
    if (this.discountType) {
      this.sharedDiscountService.getSharedDiscounts({ discountType: this.discountType } as any).subscribe(result => {
        this.sharedDiscounts = result.items || [];
        // Render the new mat-options synchronously so mat-select can match the already-set value against them
        this.changeDetectorRef.detectChanges();
      });
    }

    // Mutual exclusivity logic
    this.sharedDiscountIdentifierFormControl.valueChanges.subscribe(val => {
      const amountControl = this.form.controls.amount;
      if (val && amountControl.enabled) {
        amountControl.disable({ emitEvent: false });
      } else if (!val && amountControl.disabled) {
        amountControl.enable({ emitEvent: false });
      }
    });
    this.form.controls.amount.valueChanges.subscribe(val => {
      // When the amount input box is cleared by the user, it emits an empty string value. Use a type guard for clarity.
      if (typeof val === 'string' && val === '') {
        this.form.controls.amount.setValue(null, { emitEvent: false });
      }
      const sharedDiscountIdControl = this.sharedDiscountIdentifierFormControl;
      if (val && sharedDiscountIdControl.enabled) {
        sharedDiscountIdControl.disable({ emitEvent: false });
      } else if (!val && sharedDiscountIdControl.disabled) {
        sharedDiscountIdControl.enable({ emitEvent: false });
      }
    });

    //On edit, when loading an existing access group: If sharedDiscountId is set, disable and clear amount control and vice versa
    if (this.sharedDiscountIdentifierFormControl.value) {
      this.amountFormControl.disable({ emitEvent: false });
      this.amountFormControl.setValue(null);
    } else if (this.amountFormControl.value) {
      this.sharedDiscountIdentifierFormControl.disable({ emitEvent: false });
    }
  }

  handleCancel() {
    this.emitCancel.emit();
  }

  handleSave() {
    if (this.amountFormControl.value) {
      this.amountFormControl.setValue(this.parsePrice(this.amountFormControl.value));
    }
    const formValue = this.form.getRawValue();
    this.save.emit({
      carType: formValue.carType,
      name: formValue.name,
      amount: formValue.amount,
      identifier: this.accessGroup?.identifier,
      connectorIdentifiers: this.accessGroup?.connectorIdentifiers || [],
      sharedDiscountIdentifier: formValue.sharedDiscountIdentifier,
    });
  }

  private parsePrice(price: number): number {
    const parsedPrice = Number(price.toString().replace(',', '.'));
    return parsedPrice;
  }
}
